import { BayLevelEnum, ForeAftEnum } from "open-vessel-definition";
import { IBayPattern, getAllThePairedBays, getPairedIsoBay } from "@tedivo/tedivo-bay-grid-pure";
import { IValidationInput, IValidationOutput, IValidator, TValidatorSpecialScopes } from "../types";
import { createDictionary } from "@tedivo/tedivo-pure-helpers";

import { IIsoBayPattern } from "open-vessel-definition/build/src/models/base/types/IPositionPatterns";

const name = "allBaysWith40sArePairedValidator";
const okTranslationKey = "validators:bays.allBaysWith40sArePaired.ok";
const errorTranslationKey = "validators:bays.allBaysWith40sArePaired.error";

const specialScopes: TValidatorSpecialScopes[] = ["exportStaf"];

/**
 * Check if all bays with 40s are paired.
 *
 * A bay-level is reported in `invalidResults` (a genuine "missing pairing") when:
 * - it has 40'+ containers and is not paired, or
 * - its ABOVE and BELOW pairings differ, or
 * - it and an adjacent bay (±2) do not agree on their pairing: one points to the other
 *   and the other does not point back. Both bays are reported, even if they only hold 20s.
 *
 * An unpaired 20' only bay that no neighbour points to does not need a pairing and is
 * not reported (i.e. 005 between 001-003 and 007-009).
 */
export const allBaysWith40sArePairedValidator: IValidator<IAllBaysWith40sArePairedOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: allBaysWith40sArePairedValidatorFn,
  specialScopes,
  severity: "error",
  stage: "data",
};

export function allBaysWith40sArePairedValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IAllBaysWith40sArePairedOutput> {
  const baysData = json.baysData;
  const baysDataDict = createDictionary(baysData, (b) => `${b.isoBay}-${b.level}`);

  const { pairedBays, unpairedBays } = getAllThePairedBays(baysData, true);
  const isValid = unpairedBays.length === 0;

  const validResultsSet = new Set(
    pairedBays.flatMap(({ base, paired, level }) => [`${base}-${level}`, `${paired}-${level}`]),
  );

  const invalidResultsSet = new Set(unpairedBays.map(({ base, level }) => `${base}-${level}`));

  // If the pairing is different between ABOVE & BELOW it's not valid
  const validResultsIsoBays = pairedBays.reduce((acc, { base, paired }) => {
    acc.add(base);
    acc.add(paired);
    return acc;
  }, new Set<IBayPattern>());

  validResultsIsoBays.forEach((bay) => {
    const blAbove = baysDataDict[`${bay}-${BayLevelEnum.ABOVE}`];
    const blBelow = baysDataDict[`${bay}-${BayLevelEnum.BELOW}`];

    if (blAbove === undefined || blBelow === undefined) {
      return;
    }

    if (blAbove.pairedBay !== blBelow.pairedBay) {
      invalidResultsSet.add(`${bay}-${BayLevelEnum.ABOVE}`).add(`${bay}-${BayLevelEnum.BELOW}`);

      if (validResultsSet.has(`${bay}-${BayLevelEnum.ABOVE}`)) {
        validResultsSet.delete(`${bay}-${BayLevelEnum.ABOVE}`);
      }

      if (validResultsSet.has(`${bay}-${BayLevelEnum.BELOW}`)) {
        validResultsSet.delete(`${bay}-${BayLevelEnum.BELOW}`);
      }
    }
  });

  // If a bay is paired to an existing bay that is not paired back to it, both are invalid.
  // This applies even to 20' only bays: the unpaired one is a genuine "missing pairing".
  // A bay with no pairing, no 40s and no neighbour pointing to it needs no pairing at all.
  baysData.forEach((bl) => {
    if (!bl.pairedBay) return;

    const targetIsoBay = getPairedIsoBay(bl.isoBay, bl.pairedBay) as IBayPattern;
    const targetBl = baysDataDict[`${targetIsoBay}-${bl.level}`];
    const expectedPairedBay = bl.pairedBay === ForeAftEnum.FWD ? ForeAftEnum.AFT : ForeAftEnum.FWD;

    if (targetBl && targetBl.pairedBay !== expectedPairedBay) {
      ([`${bl.isoBay}-${bl.level}`, `${targetIsoBay}-${bl.level}`] as const).forEach((key) => {
        invalidResultsSet.add(key);
        validResultsSet.delete(key);
      });
    }
  });

  return {
    name,
    specialScopes,
    isValid,
    messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
    result: {
      validResults: new Array(...validResultsSet).map((r) => {
        const [isoBay, level] = r.split("-") as [IIsoBayPattern, BayLevelEnum];
        return {
          bay: isoBay,
          level: Number(level) as BayLevelEnum,
        };
      }),
      invalidResults: new Array(...invalidResultsSet).map((r) => {
        const [isoBay, level] = r.split("-") as [IIsoBayPattern, BayLevelEnum];
        return {
          bay: isoBay,
          level: Number(level) as BayLevelEnum,
        };
      }),
    },
  };
}

export interface IAllBaysWith40sArePairedOutput {
  validResults: IAllBaysWith40sArePairedSingleResult[];
  invalidResults: IAllBaysWith40sArePairedSingleResult[];
}

interface IAllBaysWith40sArePairedSingleResult {
  bay: IBayPattern;
  level: BayLevelEnum;
}
